<template>
  <div class="rf-field rf-range" :class="finalComputedClass" :data-min="computedMinLabel" :data-max="computedMaxLabel">
    <!-- Slot for custom label markup -->
    <slot>
      <label :for="name" class="rf-label" v-if="label">{{ label }}</label>
    </slot>

    <output class="rf-output" :class="{ 'invisible': !isDirty && !initialValue }" :for="name" v-if="showOutput" :style="outputStyle">{{ value || '-' }}</output>
    <div class="rf-input" :style="tickBackgroundSize">
      <input type="range" v-bind="{ name, min, max, step }" :disabled="disabled || invalidRange" :id="name" v-model="value" v-validate="validation" @change="onChange" ref="input" @mousedown.once="onTouch" @touchstart.once="onTouch">
      <div class="rf-slider-progress" :style="progressStyle"></div>  
      <!-- Errors -->
      <rf-message v-if="showInvalid">{{ errors.first(name) }}</rf-message>
      <transition name="fade-fast">
        <icon name="exclamation-triangle" class="rf-icon rf-icon-invalid" v-if="showInvalid" key="invalid"></icon>
      </transition>
    </div>
  </div>
</template>

<script>
import mixins from '../mixins/index'

export default {
  name: 'RfRange',
  mixins: mixins,
  props: {
    min: {
      type: [Number, String],
      default: 0
    },
    max: {
      type: [Number, String],
      default: 10
    },
    minLabel: String,
    maxLabel: String,
    step: {
      type: [Number, String],
      default: 1
    },
    initialValue: {
      type: [Number, String],
      default: null
    },
    ticks: {
      type: [Number, String],
      default: null
    },
    showOutput: {
      type: Boolean,
      default: true
    },
    showLabels: {
      type: Boolean,
      default: true
    },
    trackPointerEvents: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    normalizedMin () { return parseFloat(this.min) },
    normalizedMax () { return parseFloat(this.max) },
    normalizedStep () { return parseFloat(this.step) },
    invalidRange () {
      return this.normalizedMin > this.normalizedMax || this.normalizedMax < this.normalizedMin
    },
    computedMinLabel () {
      return this.minLabel || this.min
    },
    computedMaxLabel () {
      return this.maxLabel || this.max
    },
    position () {
      return Math.min((this.value - this.normalizedMin) / (this.normalizedMax - this.normalizedMin) * 100, 100)
    },
    computedTicks () {
      let maxTicks = 50
      return parseInt(this.ticks ? this.ticks : Math.min(Math.round((this.normalizedMax - this.normalizedMin) / this.normalizedStep), maxTicks), 10)
    },
    tickBackgroundSize () {
      return `background-size:calc((100% - 1px) / ${this.computedTicks}) 8px`
    },
    outputStyle () {
      // For best visual effect, a translateX between 0 and -80% seems best
      // though this is influenced by the value of the $inputWidthTweak in _RfRange.scss...
      return this.value ? `left:${this.position}%;transform:translateX(-${this.position * 0.8}%)` : 'left: 0'
    },
    progressStyle () {
      return `width: ${this.position}%`
    },
    finalComputedClass () {
      return Object.assign({
        'hide-ticks': this.computedTicks < 1,
        'hide-output': !this.showOutput,
        'hide-labels': !this.showLabels,
        'is-dirty': this.isDirty,
        'has-initial': this.initialValue,
        'track-pointer-events': this.trackPointerEvents
      }, this.computedClass)
    }
  },
  mounted () {
    // Set initial value
    if (this.initialValue !== null) {
      let normalizedInitialValue = parseInt(this.initialValue, 10)
      // If intial value is within range
      if (normalizedInitialValue >= this.normalizedMin && normalizedInitialValue <= this.normalizedMax) {
        this.value = normalizedInitialValue
        if (!this.disabled) {
          this.$validator.validate(this.name, this.value)
        }
      } else {
        this.value = this.normalizedMin
      }
    } else {
      this.value = this.normalizedMin
    }

    // Warn if min is less than max and vice versa
    if (this.invalidRange) {
      this.$warn('Invalid min/max values for range field:', this.name)
    }
  },
  methods: {
    onChange () {
      this.isDirty = this.fields[this.name].dirty || !!this.initialValue
      if (this.isDirty) {
        this.validate()
      }
    },
    onTouch () {
      this.isDirty = true
    }
  }
}
</script>



// WEBPACK FOOTER //
// RfRange.vue?12451f10